/**
 * TerraMatrix Universal Data Platform - useDataQuery Hook
 *
 * Sprint 5: Frontend - Data Page
 * Created: November 2025
 *
 * React hook for managing natural language query conversations.
 */

import { useState, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { dataApi, QueryResponse, TemplateQuery, TemplateVariable } from '@/services/api/dataApi';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  data?: unknown[];
  visualization?: {
    type: 'bar' | 'line' | 'pie' | 'table' | 'metric';
    config: Record<string, unknown>;
  };
  source?: {
    id: string;
    name: string;
  };
  followUpSuggestions?: string[];
  isError?: boolean;
  isLoading?: boolean;
  debug?: {
    intent?: unknown;
    matchedSources?: unknown[];
    generatedQuery?: unknown;
    executionTimeMs?: number;
  };
  /** Template query structure (for saving as reusable template) */
  templateQuery?: TemplateQuery;
  /** Extracted variables (for saving as reusable template) */
  templateVariables?: Record<string, TemplateVariable>;
  /** Whether the query was successful (used for Save as Template visibility) */
  success?: boolean;
}

const WELCOME_MESSAGE: ChatMessage = {
  id: 'welcome',
  role: 'assistant',
  content: `Welcome! I can help you query your data sources.

Try asking:
- "What's the current moisture level in zone 3?"
- "Show me temperature trends this week"
- "Compare zones 1 and 2"

Or type "connect" to add a new data source.`,
  timestamp: new Date(),
};

export function useDataQuery() {
  const [messages, setMessages] = useState<ChatMessage[]>([WELCOME_MESSAGE]);

  /**
   * Extract conversation history from messages for context
   * Filters out loading states, welcome message, and limits to last 10
   */
  const extractConversationHistory = (
    currentMessages: ChatMessage[]
  ): Array<{ role: 'user' | 'assistant'; content: string; schemaId?: string }> => {
    return currentMessages
      .filter(
        (m) =>
          !m.isLoading &&
          m.role !== 'system' &&
          m.id !== 'welcome' &&
          !m.isError // Skip error messages for context
      )
      .map((m) => ({
        role: m.role as 'user' | 'assistant',
        content: m.content,
        schemaId: m.source?.id,
      }))
      .slice(-10); // Last 10 messages for token efficiency
  };

  // Main query mutation - accepts object with query and conversation history
  const queryMutation = useMutation({
    mutationFn: ({
      query,
      conversationHistory,
    }: {
      query: string;
      conversationHistory: Array<{ role: 'user' | 'assistant'; content: string; schemaId?: string }>;
    }) => dataApi.query(query, undefined, true, conversationHistory),
    onMutate: ({ query }) => {
      // Add user message immediately
      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: query,
        timestamp: new Date(),
      };

      // Add loading message
      const loadingMessage: ChatMessage = {
        id: `loading-${Date.now()}`,
        role: 'assistant',
        content: 'Analyzing your question...',
        timestamp: new Date(),
        isLoading: true,
      };

      setMessages((prev) => [...prev, userMessage, loadingMessage]);
    },
    onSuccess: (response: QueryResponse) => {
      // Remove loading message and add response
      setMessages((prev) => {
        const withoutLoading = prev.filter((m) => !m.isLoading);

        // Check if the query was actually successful
        if (!response.success) {
          const errorMessage: ChatMessage = {
            id: `error-${Date.now()}`,
            role: 'assistant',
            content: response.error || 'Query failed. Please try again.',
            timestamp: new Date(),
            isError: true,
            followUpSuggestions: response.followUpSuggestions,
          };
          return [...withoutLoading, errorMessage];
        }

        const assistantMessage: ChatMessage = {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: response.answer || 'Query completed.',
          timestamp: new Date(),
          data: response.data,
          visualization: response.visualization,
          source: response.source,
          followUpSuggestions: response.followUpSuggestions,
          debug: response.debug,
          templateQuery: response.templateQuery,
          templateVariables: response.templateVariables,
          success: response.success,
        };

        return [...withoutLoading, assistantMessage];
      });
    },
    onError: (error: Error | { message?: string }) => {
      // Remove loading message and add error
      setMessages((prev) => {
        const withoutLoading = prev.filter((m) => !m.isLoading);

        const errorMessage: ChatMessage = {
          id: `error-${Date.now()}`,
          role: 'assistant',
          content: `Sorry, I couldn't process that query: ${error.message || 'Unknown error'}`,
          timestamp: new Date(),
          isError: true,
        };

        return [...withoutLoading, errorMessage];
      });
    },
  });

  // Send a query with conversation context
  const sendQuery = useCallback(
    (query: string) => {
      const trimmedQuery = query.trim();
      if (trimmedQuery && !queryMutation.isPending) {
        // Extract conversation history from current messages for context
        const conversationHistory = extractConversationHistory(messages);
        queryMutation.mutate({ query: trimmedQuery, conversationHistory });
      }
    },
    [queryMutation, messages]
  );

  // Clear all messages except welcome
  const clearMessages = useCallback(() => {
    setMessages([WELCOME_MESSAGE]);
  }, []);

  // Add a system message
  const addSystemMessage = useCallback((content: string) => {
    const systemMessage: ChatMessage = {
      id: `system-${Date.now()}`,
      role: 'system',
      content,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, systemMessage]);
  }, []);

  // Add a message externally (for template execution results, etc.)
  const addMessage = useCallback((message: ChatMessage) => {
    setMessages((prev) => [...prev, message]);
  }, []);

  return {
    messages,
    isQuerying: queryMutation.isPending,
    sendQuery,
    clearMessages,
    addSystemMessage,
    addMessage,
    error: queryMutation.error,
  };
}

export default useDataQuery;
